import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { CurrencyPipe } from "@angular/common";
import html2canvas from "html2canvas";
import { ExcelExportService } from "src/app/services/excelExport.service";
import { normaliseColorsForHtml2canvas } from "../../../shared/charts/html2canvas-colors";
import { ActivatedRoute } from "@angular/router";
import { combineLatest, Observable, of, Subscription } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { NavigationService } from "src/app/services/navigation.service";
import { SessionService } from "src/app/services/session.service";
import { FilterService } from "src/app/services/filter.service";
import { DashboardService } from "src/app/services/dashboard.service";
import {
  AngularResizeElementDirection,
  AngularResizeElementEvent,
} from "../../../../directives/resize-element";
import {
  DpmStaticService,
  HelpEntry,
  makeHelpLookup,
} from "../dpm-static/dpm-static.service";
import { consumeStaticDashboardEntry } from "../static-dashboard-entry";
import { shortCount } from "../short-count";
// Per-widget help text, shipped with the build — same arrangement as DPM-Static.
import HELP_INFO_JSON from "src/assets/help_info/market-access-moonlake.json";
import {
  ACCESS_DATA_URL,
  COMPETITIVE_DATA_URL,
  CompetitiveRecord,
  MOONLAKE_DATA_URL,
  MoonlakeRecord,
  normalizeAccessData,
  normalizeCompetitiveData,
  normalizeMoonlakeData,
  normalizeRweData,
  PageData,
  payerPlanLabel,
  RWE_DATA_URL,
  RxRecord,
  SwitchRecord,
  UM_DATA_URL,
  normalizeUmData,
} from "./market-access-moonlake.data";
import {
  ACCESS_KPIS,
  ACCESS_WIDGETS,
  bucketColors,
  COMPETITIVE_PARITY,
  PARITY_BETTER,
  PARITY_COLORS,
  PARITY_KEYS,
  PARITY_WORSE,
  ParityWidgetConfig,
  positionRank,
  STEP_TABLE,
  BUCKET_SHORT_LABELS,
  COVERAGE_WIDGETS,
  COVERED_COLORS,
  DATE_LABEL,
  DEFAULT_QUARTER,
  drugColours,
  FilterKind,
  FormularyTableConfig,
  FOCUS_DRUG,
  GROUPED_3L_GRADIENT,
  KpiConfig,
  KpiTile,
  legendKeysFor,
  LINE_GROUP_KEY,
  linesIn,
  MOONLAKE_FILTERS,
  MOONLAKE_PAGES,
  MoonlakeFilterDef,
  Quarter,
  quarterFromKey,
  quartersBetween,
  RWE_ACCESS_INDEX,
  RWE_KPIS,
  RWE_RESTRICTION_TABLE,
  RWE_RX_SHARE,
  RWE_SWITCHING,
  SeriesWidgetConfig,
  ShareWidgetConfig,
  SHOW_BY_OPTIONS,
  ShowBy,
  SourceScope,
  StackedWidgetConfig,
  stackKeyFor,
  stackKeysFor,
  SwitchKpiConfig,
  TABLE_PAGE_SIZE,
  TABLE_PAGE_SIZES,
  TreeLevel,
  UM_REJECTIONS,
  UM_TABLES,
  X_AXIS_OPTIONS,
  XAxis,
  Y_AXIS_OPTIONS,
  YAxis,
} from "./market-access-moonlake.config";
import {
  aggregate,
  aggregateIndex,
  aggregateSeries,
  AggRow,
  distinctValues,
  matchesWhere,
  Order,
  Where,
} from "./moonlake-aggregate";
import {
  WaterfallConfig,
  WaterfallRow,
} from "./moonlake-waterfall-chart.component";
import { SeriesChartConfig } from "./moonlake-series-chart.component";

/**
 * A filter chip backed by the distinct values in the data — the same model as
 * DPM-Static's FilterDef, so the strip, the dropdown and the side panel behave
 * identically on both screens. See that interface for the long-form notes.
 */
interface FilterDef {
  label: string;
  key: string;
  kind: FilterKind;
  single: boolean;
  /** Option order, from the filter's config. */
  order: Order<MoonlakeRecord>;
  /** Tree levels, top to bottom ("tree" only). */
  levels: TreeLevel[];
  note: string;
  /** The values: distinct field values, or — for a tree — its leaf paths. */
  options: string[];
  /** The tree built over `options` ("tree" only). */
  tree: TreeNode[];
  /** Ids of the tree nodes currently open. */
  expanded: Set<string>;
  /** Values currently filtering the data. */
  applied: Set<string>;
  /** Working selection (badge checkboxes / ng-select model). */
  checked: string[];
  search: string;
  /** "Add current selection to filter" for the interaction in progress. */
  addCurrent: boolean;
  /** What a fresh visit opens the chip on — Reset's target, isPartial's baseline. */
  defaultApplied: string[];
}

/** One node of a tree filter: a PBM, a controller under it, a plan, a formulary. */
interface TreeNode {
  /** The path to the node, levels joined by TREE_SEP; a leaf's id is its value. */
  id: string;
  label: string;
  depth: number;
  children: TreeNode[];
  /** Every leaf id at or under this node. */
  leaves: string[];
}

interface TreeRow {
  node: TreeNode;
  open: boolean;
  state: "all" | "some" | "none";
  tag: string;
}

const TREE_SEP = " › ";

interface Period {
  start: string;
  end: string;
}

interface LegendItem {
  key: string;
  label: string;
  /** CSS background for the swatch — a gradient for the grouped 3L+. */
  swatch: string;
}

/** One chart card's own state plus what it renders. */
interface CoverageCard {
  kind: "coverage";
  widget: StackedWidgetConfig;
  showBy: ShowBy;
  grouped: boolean;
  xAxis: XAxis;
  yAxis: YAxis;
  /** The Settings popover's working values, committed on Apply. */
  pendingX: XAxis;
  pendingY: YAxis;
  hidden: Set<string>;
  rows: WaterfallRow[];
  config: WaterfallConfig;
  legend: LegendItem[];
  help: HelpEntry | null;
}

/** A page-2 / page-4 card: grouped bars or lines (moonlake-series-chart). */
interface SeriesCard {
  kind: "series";
  widget: SeriesWidgetConfig;
  /** The Show-by value; "" for all of them. */
  showBy: string;
  showByOptions: { value: string; title: string }[];
  xAxis: string;
  yAxis: string;
  pendingX: string;
  pendingY: string;
  hidden: Set<string>;
  rows: AggRow[];
  config: SeriesChartConfig;
  legend: LegendItem[];
  help: HelpEntry | null;
}

/** A page-3 card: drugs' lives at / below the competitor set's position (stacked, as page 1). */
interface ParityCard {
  kind: "parity";
  widget: ParityWidgetConfig;
  xAxis: string;
  yAxis: YAxis;
  pendingX: string;
  pendingY: YAxis;
  hidden: Set<string>;
  rows: WaterfallRow[];
  config: WaterfallConfig;
  legend: LegendItem[];
  help: HelpEntry | null;
}

/** A page-4 card: a non-MMIT measure stacked by a field (the waterfall chart, as page 1). */
interface ShareCard {
  kind: "share";
  widget: ShareWidgetConfig;
  /** The measure the bands sum (a `measures` key). */
  showBy: string;
  xAxis: string;
  yAxis: YAxis;
  pendingX: string;
  pendingY: YAxis;
  hidden: Set<string>;
  rows: WaterfallRow[];
  config: WaterfallConfig;
  legend: LegendItem[];
  help: HelpEntry | null;
}

type AnyCard = CoverageCard | SeriesCard | ParityCard | ShareCard;

/** One plan / formulary row of the step-through table. */
interface StepRow {
  /** PBM › Controller › Plan › Formulary. */
  path: string;
  controller: string;
  planFormulary: string;
  indication: string;
  lives: number;
  coverage: string;
  position: string;
  stepText: string;
}

/** One formulary of a page-5 table: its cells, per the table's config. */
interface FormularyTableRow {
  /** PBM › Controller › Plan › Formulary. */
  path: string;
  indication: string;
  lives: number;
  title: string;
  cells: Record<string, string>;
}

/** A grid's paging: rows per page and the page shown (0-based). */
interface Pager {
  size: number;
  index: number;
}

/** A page-5 table: its config, the drug picked and the rows for it. */
interface FormularyTableView {
  cfg: FormularyTableConfig;
  drug: string;
  drugOptions: string[];
  rows: FormularyTableRow[];
  pager: Pager;
  help: HelpEntry | null;
}

/** One plan of the restriction-index table (page 4). */
interface RestrictionRow {
  /** "<controller> / <plan>". */
  plan: string;
  /** PBM › Controller › Plan. */
  path: string;
  requests: number;
  restricted: number;
  /** restricted ÷ requests. */
  index: number;
  status: string;
}

interface KpiView<C extends KpiTile = KpiTile> {
  cfg: C;
  /** Percent, or null when there is nothing to measure under the filters. */
  value: number | null;
  /** The figure's colour (the config's, or its miss colour below target). */
  color: string;
  /** Shown with a sign (+1.8%). */
  signed?: boolean;
  /** "Target: ≥0%", under the label. */
  target?: string;
  help: HelpEntry | null;
}

interface SavedCard {
  showBy?: string;
  grouped?: boolean;
  xAxis?: string;
  yAxis?: string;
  /** A grid's rows per page. */
  pageSize?: number;
}

interface SavedState {
  filters?: Record<string, string[]>;
  period?: Period;
  page?: number;
  cards?: Record<string, SavedCard>;
}

const helpFor = makeHelpLookup(HELP_INFO_JSON as HelpEntry[]);

// ===========================================================================
// Market Access – Moonlake (static)
//
// Same shell as DPM-Static — topnav, accent line, the "Selected filters" strip
// with its chips and Reset, the right-hand filter panel — with three pages under
// it. The chips' values are the MMIT dataset's distinct values
// (assets/json/market-access-moonlake-data.json; Payer as a PBM › Controller ›
// Plan › Formulary tree), Date is a quarter range over its snapshots, and every
// widget re-aggregates client-side on a change.
//
// Page 1, Coverage by Payer/PBM and Drug: one card per indication (HS, PsA,
// PsO), each a stacked bar of lives by Biologics Count that opens into a
// waterfall when a bar (or its label) is clicked.
// Page 2, Access & Coverage: KPI rates, access parity, analog launch curves and
// the claims addressable market.
// Page 3, Competitive Position: relative position vs. the competitor set and
// step-through requirements by plan.
// Page 4, Real-World Evidence: switching win/loss, the relative payer access
// and restriction indexes per plan, NBRx/TRx share and competitor switching.
// Page 5, Utilization Management: custom formularies' deviation from their
// PBM's national one, UM detail by plan and rejection reasons by channel.
// ===========================================================================
@Component({
  selector: "app-market-access-moonlake",
  templateUrl: "./market-access-moonlake.component.html",
  styleUrls: ["./market-access-moonlake.component.scss"],
  // Screen-scoped, as on DPM-Static: used here only to publish the page key.
  // ExcelExportService (and the CurrencyPipe it formats numbers with) is not
  // providedIn root, so the screen provides its own, as the chart widgets do.
  providers: [DpmStaticService, ExcelExportService, CurrencyPipe],
  standalone: false,
})
export class MarketAccessMoonlakeComponent implements OnInit, OnDestroy {
  readonly AngularResizeElementDirection = AngularResizeElementDirection;

  clientLogo: any;
  newColor: any;
  moduleName: any;
  clientKey: any;
  moduleKey: any;
  dashboardKey: any;

  dashboardTitle = "Market Access - Moonlake";
  readonly topnavHelpInfo = {
    pageInfoChecks: true,
    pageInfoVar: "Market_Access_Moonlake_User_Manual.pdf",
    pageInfoApiKey: "",
    pageInfoWidgetKey: "",
  };

  // ---- data ----
  loading = true;
  private data: MoonlakeRecord[] = [];
  private viewData: MoonlakeRecord[] = [];
  /** The open page's non-MMIT records, by source (see market-access-moonlake.data.ts). */
  private sourceData: PageData["sources"] = {};
  /** Each page's dataset once loaded, by page key — a page's file is fetched once. */
  private pageData = new Map<string, PageData>();
  /** Channels of the selected formularies, or null while Payer is on All. */
  private payerChannels: Set<string> | null = null;

  // ---- pages ----
  readonly pages = MOONLAKE_PAGES;
  activePage = 0;

  // ---- filter strip + dropdown + side panel (as on DPM-Static) ----
  filters: FilterDef[] = MOONLAKE_FILTERS.map((d: MoonlakeFilterDef) => ({
    label: d.label,
    key: d.key,
    kind: d.kind,
    single: !!d.single,
    order: d.order || "alpha",
    levels: d.levels || [],
    note: d.note || "",
    options: [],
    tree: [],
    expanded: new Set<string>(),
    applied: new Set<string>(),
    checked: [],
    search: "",
    addCurrent: false,
    defaultApplied: [],
  }));
  openKey: string | null = null;
  menuPos = { left: 0, top: 0 };
  itemsShowLimit = 2;
  badgesOverflow = false;
  filterPanelOpen = false;
  panelWidth = 380;
  @ViewChild("badgeScroller") badgeScroller?: ElementRef<HTMLElement>;

  // ---- Date: a contiguous range of quarters ----
  readonly dateLabel = DATE_LABEL;
  readonly PERIOD_KEY = "period";
  /** Every quarter from the first snapshot to the last. */
  quarters: Quarter[] = [];
  period: Period = { start: "", end: "" };
  private defaultPeriod: Period = { start: "", end: "" };
  /** The Date dropdown's working range, committed on Apply. */
  pendingPeriod: Period = { start: "", end: "" };
  /** Distinct snapshots, chronological. */
  private snapshots: { name: string; dt: string }[] = [];

  // ---- page 1 ----
  readonly showByOptions = SHOW_BY_OPTIONS;
  readonly xAxisOptions = X_AXIS_OPTIONS;
  readonly yAxisOptions = Y_AXIS_OPTIONS;
  cards: CoverageCard[] = [];

  // ---- page 2 ----
  kpis: KpiView<KpiConfig>[] = [];
  seriesCards: SeriesCard[] = [];

  // ---- page 3 ----
  parityCards: ParityCard[] = [];
  readonly stepTable = STEP_TABLE;
  readonly stepHelp = helpFor(STEP_TABLE.title);
  stepDrug = STEP_TABLE.defaultDrug;
  stepDrugOptions: string[] = [];
  stepRows: StepRow[] = [];
  /** More than one indication in the table: it gets an Indication column. */
  stepShowIndication = false;
  stepPager: Pager = { size: TABLE_PAGE_SIZE, index: 0 };

  // ---- grids ----
  readonly pageSizes = TABLE_PAGE_SIZES;

  // ---- page 4 ----
  rweKpis: KpiView<SwitchKpiConfig>[] = [];
  rweIndexCard!: SeriesCard;
  rweSwitchCard!: SeriesCard;
  shareCards: ShareCard[] = [];
  readonly restrictionTable = RWE_RESTRICTION_TABLE;
  readonly restrictionHelp = helpFor(RWE_RESTRICTION_TABLE.title);
  restrictionRows: RestrictionRow[] = [];
  restrictionPager: Pager = { size: TABLE_PAGE_SIZE, index: 0 };

  // ---- page 5 ----
  formularyTables: FormularyTableView[] = [];

  /** Card currently in browser fullscreen, if any. */
  fullscreenId: string | null = null;
  /** Help popover content for whichever card's icon was clicked. */
  cardHelp: HelpEntry | null = null;

  private savedState: SavedState = {};
  private stateReady = false;
  private reqSubcription: Subscription[] = [];

  constructor(
    private sessionService: SessionService,
    private router: ActivatedRoute,
    private navigationService: NavigationService,
    private dashboardService: DashboardService,
    private filterService: FilterService,
    private dpmService: DpmStaticService,
    private http: HttpClient,
    private excelService: ExcelExportService,
    private cdr: ChangeDetectorRef
  ) {
    this.reqSubcription.push(
      combineLatest([this.router.params, this.router.queryParams]).subscribe(
        ([p, params]: [any, any]) => {
          this.moduleName = p["module"] || "";
          const decryptedParams = this.navigationService.decryptData(params);
          this.clientKey = decryptedParams["cl_key"];
          this.moduleKey = decryptedParams["md_key"];
          this.dashboardKey = decryptedParams["ds_key"];
          this.dashboardService.moduleKey = this.moduleKey;
          // markForCheck, not detectChanges — this first runs inside the
          // constructor (see DpmStaticComponent for the full story).
          this.cdr.markForCheck();
        }
      )
    );
  }

  ngOnInit(): void {
    this.clientLogo = this.sessionService.getClintLogo();
    this.newColor = this.sessionService.getColorCodes();

    // Opened from its card: start on the defaults rather than the last visit.
    if (consumeStaticDashboardEntry(this.dashboardKey)) this.clearState();
    this.savedState = this.readState();

    const page = this.savedState.page;
    if (page !== undefined && this.pages[page]) this.activePage = page;

    this.cards = COVERAGE_WIDGETS.map((widget) => {
      const s = this.savedState.cards?.[widget.id];
      const showBy = SHOW_BY_OPTIONS.find((o) => o.value === s?.showBy)?.value || "group3l";
      const xAxis = X_AXIS_OPTIONS.find((o) => o.key === s?.xAxis)?.key || widget.xAxis;
      const yAxis = Y_AXIS_OPTIONS.find((o) => o.key === s?.yAxis)?.key || widget.yAxis;
      return {
        kind: "coverage" as const,
        widget,
        showBy,
        grouped: s?.grouped ?? true,
        xAxis,
        yAxis,
        pendingX: xAxis,
        pendingY: yAxis,
        hidden: new Set<string>(),
        rows: [],
        config: null as any,
        legend: [],
        help: helpFor(widget.title),
      };
    });

    this.kpis = ACCESS_KPIS.map((cfg) => ({ cfg, value: null, color: cfg.color, help: helpFor(cfg.label) }));
    this.seriesCards = ACCESS_WIDGETS.map((widget) => this.seriesCardFor(widget));

    this.rweKpis = RWE_KPIS.map((cfg) => ({
      cfg,
      value: null,
      color: cfg.color,
      signed: true,
      target: `Target: ≥${cfg.target}%`,
      help: helpFor(cfg.label),
    }));
    this.rweIndexCard = this.seriesCardFor(RWE_ACCESS_INDEX);
    this.rweSwitchCard = this.seriesCardFor(RWE_SWITCHING);
    this.formularyTables = UM_TABLES.map((cfg) => ({
      cfg,
      drug: this.savedState.cards?.[cfg.id]?.showBy || cfg.defaultDrug,
      drugOptions: [],
      rows: [],
      pager: this.savedPager(cfg.id),
      help: helpFor(cfg.title),
    }));
    this.shareCards = [RWE_RX_SHARE, UM_REJECTIONS].map((widget) => {
      const s = this.savedState.cards?.[widget.id];
      const xAxis = widget.xAxes.find((o) => o.key === s?.xAxis)?.key || widget.xAxis;
      const yAxis = widget.yAxes.find((o) => o.key === s?.yAxis)?.key || widget.yAxis;
      return {
        kind: "share" as const,
        widget,
        showBy: widget.measures.find((m) => m.key === s?.showBy)?.key || widget.measures[0].key,
        xAxis,
        yAxis,
        pendingX: xAxis,
        pendingY: yAxis,
        hidden: new Set<string>(),
        rows: [],
        config: null as any,
        legend: [],
        help: helpFor(widget.title),
      };
    });

    this.parityCards = [COMPETITIVE_PARITY].map((widget) => {
      const s = this.savedState.cards?.[widget.id];
      const xAxis = widget.xAxes.find((o) => o.key === s?.xAxis)?.key || widget.xAxis;
      const yAxis = Y_AXIS_OPTIONS.find((o) => o.key === s?.yAxis)?.key || widget.yAxis;
      return {
        kind: "parity" as const,
        widget,
        xAxis,
        yAxis,
        pendingX: xAxis,
        pendingY: yAxis,
        hidden: new Set<string>(),
        rows: [],
        config: null as any,
        legend: [],
        help: helpFor(widget.title),
      };
    });
    this.stepDrug = this.savedState.cards?.[STEP_TABLE.id]?.showBy || STEP_TABLE.defaultDrug;
    this.stepPager = this.savedPager(STEP_TABLE.id);
    this.restrictionPager = this.savedPager(RWE_RESTRICTION_TABLE.id);

    this.dpmService.publishPageKey(this.dashboardKey, this.activePage + 1);
    this.loadData();
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((s) => s.unsubscribe());
  }

  /** A series card on its saved settings, or the widget's defaults. */
  private seriesCardFor(widget: SeriesWidgetConfig): SeriesCard {
    const s = this.savedState.cards?.[widget.id];
    const xAxis = widget.xAxes.find((o) => o.key === s?.xAxis)?.key || widget.xAxis;
    const yAxis = widget.yAxes.find((o) => o.key === s?.yAxis)?.key || widget.yAxis;
    return {
      kind: "series",
      widget,
      showBy: s?.showBy || "",
      showByOptions: [],
      xAxis,
      yAxis,
      pendingX: xAxis,
      pendingY: yAxis,
      hidden: new Set<string>(),
      rows: [],
      config: null as any,
      legend: [],
      help: helpFor(widget.title),
    };
  }

  // ================================================================== load
  /**
   * The active page's one dataset — fetched on first visit, cached after. A
   * file that fails loads as empty. Every page's file carries the MMIT records,
   * so the filters and the Date range read the same values on each.
   */
  private loadData(): void {
    const key = this.pages[this.activePage]?.key;
    const cached = this.pageData.get(key);
    if (cached) {
      this.useData(cached);
      return;
    }
    this.loading = true;
    this.reqSubcription.push(
      this.fetchPageData(key).subscribe((data) => {
        this.pageData.set(key, data);
        // Another page may have been picked while this one loaded.
        if (this.pages[this.activePage]?.key === key) this.useData(data);
      })
    );
  }

  private fetchPageData(key: string): Observable<PageData> {
    const empty: PageData = { mmit: [], sources: {} };
    const get = (url: string, normalize: (p: any) => PageData): Observable<PageData> =>
      this.http.get<any>(url).pipe(
        map(normalize),
        catchError(() => of(empty))
      );
    switch (key) {
      case "access":
        return get(ACCESS_DATA_URL, normalizeAccessData);
      case "competitive":
        return get(COMPETITIVE_DATA_URL, normalizeCompetitiveData);
      case "rwe":
        return get(RWE_DATA_URL, normalizeRweData);
      case "um":
        return get(UM_DATA_URL, normalizeUmData);
      default:
        return get(MOONLAKE_DATA_URL, (p) => ({ ...empty, mmit: normalizeMoonlakeData(p) }));
    }
  }

  private useData(data: PageData): void {
    this.data = data.mmit;
    this.sourceData = data.sources;
    this.onDataLoaded();
  }

  private onDataLoaded(): void {
    this.stateReady = false;
    this.initPeriod();
    this.cascadeFilters(); // every chip on its default over the full data
    this.restoreFilterState(); // then the saved selection, where still valid
    this.cascadeFilters(); // and narrow the children to the restored parents
    this.loading = false;
    this.applyFilters();
    this.publishTopnavContext();
    this.stateReady = true;
    this.updateBadgeOverflow();
  }

  // =================================================================== pages
  selectPage(i: number): void {
    if (i === this.activePage) return;
    this.activePage = i;
    // Each page has its own page key (…11, …21, …31), as a dynamic dashboard's do.
    this.dpmService.publishPageKey(this.dashboardKey, i + 1);
    this.saveState();
    this.loadData();
  }

  // ======================================================= filter machinery
  // Ported from DPM-Static, minus what this screen has no use for (dynamic API
  // filters, the date range and cross-filtering).

  hasOptions(f: FilterDef): boolean {
    return f.options.length > 0;
  }

  filterPlaceholder(f: FilterDef): string {
    return "Select " + f.label;
  }

  /** A record's value for a chip: its field, or — for a tree — its leaf path. */
  private valueOf(f: FilterDef, r: MoonlakeRecord): string {
    if (f.kind === "tree") {
      return f.levels.map((l) => String(r[l.field] ?? "")).join(TREE_SEP);
    }
    return String(r[f.key as keyof MoonlakeRecord] ?? "");
  }

  private rowPasses(f: FilterDef, r: MoonlakeRecord): boolean {
    if (!this.hasOptions(f) || f.kind === "display") return true;
    const v = this.valueOf(f, r);
    if (!v) return true;
    return f.applied.has(v);
  }

  /**
   * Narrow each chip's values to what its PARENTS (the chips before it) still
   * allow — DPM-Static's cascade, rule for rule: only an explicit narrowing
   * survives a parent change, and only as far as the new list allows.
   */
  private cascadeFilters(): void {
    let rows = this.data;
    this.filters.forEach((f) => {
      const options =
        f.kind === "tree"
          ? this.buildTree(f, rows)
          : distinctValues(rows, f.key as keyof MoonlakeRecord, f.order);
      const defaultApplied = f.single ? options.slice(0, 1) : options;
      const onDefault = this.sameSelection([...f.applied], f.defaultApplied);
      const kept = onDefault ? [] : [...f.applied].filter((v) => options.includes(v));
      f.options = options;
      f.defaultApplied = [...defaultApplied];
      f.applied = new Set(kept.length ? kept : defaultApplied);
      f.checked = [...f.applied];
      rows = rows.filter((r) => this.rowPasses(f, r));
    });
  }

  private sameSelection(a: any[], b: any[]): boolean {
    if (a.length !== b.length) return false;
    const bs = new Set(b.map((v) => String(v)));
    return a.every((v) => bs.has(String(v)));
  }

  /** Filter the dataset by every chip and the Date range, then rebuild every widget. */
  private applyFilters(): void {
    const inPeriod = new Set(this.snapshotsIn(this.period));
    this.viewData = this.data.filter(
      (r) => inPeriod.has(r.snapshot) && this.filters.every((f) => this.rowPasses(f, r))
    );
    this.payerChannels = this.channelsOfPayer();
    // Only the active page's widgets: the others' data is not loaded. A page is
    // rebuilt over its own dataset when it is opened.
    switch (this.pages[this.activePage]?.key) {
      case "access":
        this.buildKpis();
        this.seriesCards.forEach((c) => this.buildSeriesCard(c));
        break;
      case "competitive":
        this.parityCards.forEach((c) => this.buildParityCard(c));
        this.buildStepTable();
        break;
      case "rwe":
        this.buildRweKpis();
        this.buildSeriesCard(this.rweIndexCard);
        this.buildRestrictionTable();
        this.shareCardsOn("rwe").forEach((c) => this.buildShareCard(c));
        this.buildSeriesCard(this.rweSwitchCard);
        break;
      case "um":
        this.formularyTables.forEach((t) => this.buildFormularyTable(t));
        this.shareCardsOn("um").forEach((c) => this.buildShareCard(c));
        break;
      default:
        this.cards.forEach((c) => this.buildCard(c));
    }
  }

  /**
   * The channels the Payer selection spans, for sources that know the channel
   * but not the formulary (claims). Null while Payer is on its default.
   */
  private channelsOfPayer(): Set<string> | null {
    const f = this.filters.find((x) => x.kind === "tree");
    if (!f || !this.isPartial(f)) return null;
    return new Set(this.data.filter((r) => f.applied.has(this.valueOf(f, r))).map((r) => r.channel));
  }

  // ---- tree filter (Payer: PBM › Controller › Plan › Formulary) ----
  /** Build `f.tree` over `rows`; returns the leaf ids in tree order — the chip's values. */
  private buildTree(f: FilterDef, rows: MoonlakeRecord[]): string[] {
    const root: TreeNode = { id: "", label: "", depth: -1, children: [], leaves: [] };
    const index = new Map<string, TreeNode>();
    rows.forEach((r) => {
      let parent = root;
      const parts: string[] = [];
      for (let d = 0; d < f.levels.length; d++) {
        const label = String(r[f.levels[d].field] ?? "");
        if (!label) return;
        parts.push(label);
        const id = parts.join(TREE_SEP);
        let node = index.get(id);
        if (!node) {
          node = { id, label, depth: d, children: [], leaves: [] };
          index.set(id, node);
          parent.children.push(node);
        }
        parent = node;
      }
    });
    const finish = (n: TreeNode): string[] => {
      n.children.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      n.leaves = n.children.length ? n.children.flatMap(finish) : [n.id];
      return n.leaves;
    };
    finish(root);
    f.tree = root.children;
    return root.children.length ? root.leaves : [];
  }

  /**
   * The rows the tree draws, in order. Without a search: every top node, and
   * children of open nodes. With one: nodes whose name matches, their ancestors
   * (opened to reveal them) and — when opened — what sits under a match.
   */
  treeRows(f: FilterDef): TreeRow[] {
    const s = f.search.trim().toLowerCase();
    const checked = new Set(f.checked);
    const hit = (n: TreeNode) => n.label.toLowerCase().includes(s);
    const descendantHit = (n: TreeNode): boolean => n.children.some((c) => hit(c) || descendantHit(c));
    const out: TreeRow[] = [];
    const walk = (nodes: TreeNode[], underHit: boolean) =>
      nodes.forEach((n) => {
        const self = !s || underHit || hit(n);
        const below = !!s && descendantHit(n);
        if (!self && !below) return;
        const open = f.expanded.has(n.id) || (below && !hit(n));
        const on = n.leaves.filter((l) => checked.has(l)).length;
        out.push({
          node: n,
          open,
          state: on === 0 ? "none" : on === n.leaves.length ? "all" : "some",
          tag: f.levels[n.depth]?.tag ? `(${f.levels[n.depth].label})` : "",
        });
        if (open) walk(n.children, underHit || (!!s && hit(n)));
      });
    walk(f.tree, false);
    return out;
  }

  /** Leaves under every node whose name matches the search; all leaves without one. */
  private searchLeaves(f: FilterDef, s: string): string[] {
    const set = new Set<string>();
    const walk = (nodes: TreeNode[]) =>
      nodes.forEach((n) => {
        if (n.label.toLowerCase().includes(s)) n.leaves.forEach((l) => set.add(l));
        else walk(n.children);
      });
    walk(f.tree);
    return f.options.filter((o) => set.has(o));
  }

  /** Tick a node: all of its leaves on, or — when they all are — off. */
  toggleNode(f: FilterDef, n: TreeNode, ev?: Event): void {
    ev?.stopPropagation();
    const set = new Set(f.checked);
    const all = n.leaves.every((l) => set.has(l));
    n.leaves.forEach((l) => (all ? set.delete(l) : set.add(l)));
    f.checked = f.options.filter((o) => set.has(o));
  }

  toggleExpand(f: FilterDef, n: TreeNode, ev?: Event): void {
    ev?.stopPropagation();
    const next = new Set(f.expanded);
    next.has(n.id) ? next.delete(n.id) : next.add(n.id);
    f.expanded = next;
  }

  /** Open the tree down to every partly-selected node, so a narrowing is visible on open. */
  private expandPartial(f: FilterDef): void {
    const applied = f.applied;
    const open = new Set<string>();
    const walk = (nodes: TreeNode[]) =>
      nodes.forEach((n) => {
        const on = n.leaves.filter((l) => applied.has(l)).length;
        if (on > 0 && on < n.leaves.length) {
          open.add(n.id);
          walk(n.children);
        }
      });
    walk(f.tree);
    f.expanded = open;
  }

  trackTreeRow = (_: number, r: TreeRow) => r.node.id;

  // ---- Date ----
  /** Quarters and snapshots from the data; the default range from config. */
  private initPeriod(): void {
    const byName = new Map<string, string>();
    this.data.forEach((r) => {
      if (!r.snapshot || !r.snapshot_dt) return;
      const dt = byName.get(r.snapshot);
      if (!dt || r.snapshot_dt < dt) byName.set(r.snapshot, r.snapshot_dt);
    });
    this.snapshots = [...byName.entries()]
      .map(([name, dt]) => ({ name, dt }))
      .sort((a, b) => a.dt.localeCompare(b.dt));
    const dts = this.snapshots.map((s) => s.dt);
    this.quarters = quartersBetween(dts[0], dts[dts.length - 1]);

    const keys = this.quarters.map((q) => q.key);
    const def = keys.includes(DEFAULT_QUARTER) ? DEFAULT_QUARTER : keys[keys.length - 1] || "";
    this.defaultPeriod = { start: def, end: def };

    const saved = this.savedState.period;
    this.period =
      saved && keys.includes(saved.start) && keys.includes(saved.end) && saved.start <= saved.end
        ? { ...saved }
        : { ...this.defaultPeriod };
  }

  /**
   * Snapshots a range reads: those dated inside it, or — when none is — the
   * latest one on or before its end (MMIT's "current" value at that date).
   */
  snapshotsIn(p: Period): string[] {
    if (!p.start || !p.end) return [];
    const from = quarterFromKey(p.start).start;
    const to = quarterFromKey(p.end).end;
    const inside = this.snapshots.filter((s) => s.dt >= from && s.dt <= to);
    if (inside.length) return inside.map((s) => s.name);
    const before = this.snapshots.filter((s) => s.dt <= to);
    return before.length ? [before[before.length - 1].name] : [];
  }

  periodLabel(p: Period): string {
    if (!p.start || !p.end) return "";
    const a = quarterFromKey(p.start).label;
    return p.start === p.end ? a : `${a} – ${quarterFromKey(p.end).label}`;
  }

  get periodOpen(): boolean {
    return this.openKey === this.PERIOD_KEY;
  }

  get periodPartial(): boolean {
    return (
      this.period.start !== this.defaultPeriod.start || this.period.end !== this.defaultPeriod.end
    );
  }

  /** The quarters the To list offers: the From quarter and later. */
  get endQuarters(): Quarter[] {
    return this.quarters.filter((q) => q.key >= this.pendingPeriod.start);
  }

  /** The snapshot the Drug / Channel views will read for the working range. */
  get pendingSnapshot(): string {
    const s = this.snapshotsIn(this.pendingPeriod);
    return s[s.length - 1] || "none";
  }

  togglePeriod(ev: MouseEvent): void {
    ev.stopPropagation();
    if (!this.quarters.length) return;
    if (this.periodOpen) {
      this.openKey = null;
      return;
    }
    this.pendingPeriod = { ...this.period };
    this.placeMenu(ev, 280);
    this.openKey = this.PERIOD_KEY;
  }

  onPendingStart(key: string): void {
    this.pendingPeriod = {
      start: key,
      end: this.pendingPeriod.end < key ? key : this.pendingPeriod.end,
    };
  }

  applyPeriod(): void {
    const { start, end } = this.pendingPeriod;
    this.period = start <= end ? { start, end } : { start: end, end: start };
    this.openKey = null;
    this.applyFilters();
    this.saveState();
    this.updateBadgeOverflow();
  }

  /**
   * The chip's badge: applied/total, as on DPM-Static. The one exception is the
   * hierarchy (Payer), which reads "All" while every node is selected.
   */
  chipValue(f: FilterDef): string {
    if (f.kind === "tree" && f.applied.size === f.options.length) return "All";
    return `${f.applied.size}/${f.options.length}`;
  }

  private onFilterApplied(f: FilterDef): void {
    this.cascadeFilters();
    this.applyFilters();
    this.saveState();
    this.updateBadgeOverflow();
  }

  // ---- badge dropdown ----
  toggleDropdown(f: FilterDef, ev: MouseEvent): void {
    ev.stopPropagation();
    if (!this.hasOptions(f)) return;
    if (this.openKey === f.key) {
      this.openKey = null;
      return;
    }
    f.checked = [...f.applied];
    f.search = "";
    f.addCurrent = false;
    if (f.kind === "tree") this.expandPartial(f);
    this.placeMenu(ev, f.kind === "tree" ? 330 : 300);
    this.openKey = f.key;
  }

  /** Put the dropdown under the element clicked, kept on screen. */
  private placeMenu(ev: MouseEvent, width: number): void {
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    this.menuPos = {
      left: Math.max(12, Math.min(r.left, window.innerWidth - width - 12)),
      top: r.bottom + 4,
    };
  }

  closeDropdown(): void {
    this.openKey = null;
  }

  get openFilter(): FilterDef | undefined {
    return this.filters.find((f) => f.key === this.openKey);
  }

  filteredOptions(f: FilterDef): string[] {
    const s = f.search.trim().toLowerCase();
    if (!s) return f.options;
    if (f.kind === "tree") return this.searchLeaves(f, s);
    return f.options.filter((o) => o.toLowerCase().includes(s));
  }

  isChecked(f: FilterDef, opt: string): boolean {
    return f.checked.includes(opt);
  }

  /** A tick on a multi-select; a direct pick (applied at once) on a single-select. */
  toggleOption(f: FilterDef, opt: string, ev?: Event): void {
    ev?.stopPropagation();
    if (f.single) {
      f.checked = [opt];
      f.applied = new Set([opt]);
      this.commitFilter(f);
      return;
    }
    f.checked = f.checked.includes(opt)
      ? f.checked.filter((x) => x !== opt)
      : [...f.checked, opt];
  }

  allChecked(f: FilterDef): boolean {
    const visible = this.filteredOptions(f);
    return visible.length > 0 && visible.every((o) => f.checked.includes(o));
  }

  toggleSelectAll(f: FilterDef, ev?: Event): void {
    ev?.stopPropagation();
    const visible = this.filteredOptions(f);
    f.checked = this.allChecked(f)
      ? f.checked.filter((o) => !visible.includes(o))
      : [...new Set([...f.checked, ...visible])];
  }

  toggleAddCurrent(f: FilterDef, ev?: Event): void {
    ev?.stopPropagation();
    f.addCurrent = !f.addCurrent;
    if (f.addCurrent) {
      f.checked = [...new Set([...f.applied, ...f.checked])];
      return;
    }
    const visible = this.filteredOptions(f);
    f.checked = f.checked.filter((o) => visible.includes(o));
  }

  private includeValues(f: FilterDef): string[] {
    if (f.addCurrent) return [...new Set([...f.applied, ...f.checked])];
    const visible = this.filteredOptions(f);
    return f.checked.filter((o) => visible.includes(o));
  }

  canInclude(f: FilterDef): boolean {
    return this.includeValues(f).length > 0;
  }
  canExclude(f: FilterDef): boolean {
    return f.checked.length > 0 && f.checked.length < f.options.length;
  }
  canClear(f: FilterDef): boolean {
    return this.hasOptions(f) && f.checked.length < f.options.length;
  }

  applyInclude(f: FilterDef, sc?: { close?: () => void }): void {
    f.applied = new Set(this.includeValues(f));
    this.commitFilter(f, sc);
  }

  applyExclude(f: FilterDef, sc?: { close?: () => void }): void {
    f.applied = new Set(f.options.filter((o) => !f.checked.includes(o)));
    this.commitFilter(f, sc);
  }

  applyClear(f: FilterDef, sc?: { close?: () => void }): void {
    f.checked = [...f.options];
    f.applied = new Set(f.options);
    this.commitFilter(f, sc);
  }

  private commitFilter(f: FilterDef, sc?: { close?: () => void }): void {
    f.search = "";
    f.addCurrent = false;
    this.openKey = null;
    this.onFilterApplied(f);
    sc?.close?.();
  }

  /** Closing an ng-select without applying reverts to what is applied. */
  onSelectClose(f: FilterDef): void {
    f.checked = [...f.applied];
    f.search = "";
    f.addCurrent = false;
  }

  onFilterText(f: FilterDef, ev: Event): string {
    const value = (ev.target as HTMLInputElement).value;
    f.search = value;
    return value;
  }

  onSingleModel(f: FilterDef, val: string | null): void {
    f.checked = val ? [val] : [];
    if (!val) return;
    f.applied = new Set([val]);
    this.commitFilter(f);
  }

  isPartial(f: FilterDef): boolean {
    if (!this.hasOptions(f) || f.kind === "display") return false;
    return !this.sameSelection([...f.applied], f.defaultApplied);
  }

  get anyPartial(): boolean {
    return this.periodPartial || this.filters.some((f) => this.isPartial(f));
  }

  /** Reset to default: every chip and the Date range back to what a fresh visit opens on. */
  resetFilters(): void {
    if (!this.anyPartial) return;
    this.filters.forEach((f) => {
      if (!this.isPartial(f)) return;
      f.applied = new Set(f.defaultApplied);
      f.checked = [...f.applied];
    });
    this.period = { ...this.defaultPeriod };
    this.openKey = null;
    this.cascadeFilters();
    this.applyFilters();
    this.saveState();
    this.updateBadgeOverflow();
  }

  // ---- strip scrolling ----
  scrollBadges(dir: number): void {
    this.badgeScroller?.nativeElement?.scrollBy({ left: dir * 220, behavior: "smooth" });
  }

  private updateBadgeOverflow(): void {
    setTimeout(() => {
      const el = this.badgeScroller?.nativeElement;
      this.badgesOverflow = el ? el.scrollWidth > el.clientWidth + 2 : false;
    });
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.updateBadgeOverflow();
    // A card in full view follows the window's height.
    if (this.fullscreenId) this.refitFullView();
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.openKey = null;
    this.filterPanelOpen = false;
    const full = this.findCard(this.fullscreenId);
    if (full) this.toggleFullscreen(full);
  }

  private get allCards(): AnyCard[] {
    const rwe = this.rweIndexCard ? [this.rweIndexCard, this.rweSwitchCard] : [];
    return [...this.cards, ...this.seriesCards, ...this.parityCards, ...rwe, ...this.shareCards];
  }

  private findCard(id: string | null): AnyCard | undefined {
    return id ? this.allCards.find((c) => c.widget.id === id) : undefined;
  }

  private rebuild(card: AnyCard): void {
    if (card.kind === "coverage") this.buildCard(card);
    else if (card.kind === "series") this.buildSeriesCard(card);
    else if (card.kind === "share") this.buildShareCard(card);
    else this.buildParityCard(card);
  }

  // ---- right-side filter panel ----
  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
    this.openKey = null;
    if (this.filterPanelOpen) {
      this.filters.forEach((f) => (f.checked = [...f.applied]));
    }
  }

  closeFilterPanel(): void {
    this.filterPanelOpen = false;
  }

  onResizeSide(evt: AngularResizeElementEvent): void {
    this.panelWidth = evt.currentWidthValue;
  }

  /**
   * The snapshot the formulary views read: summing lives across MMIT snapshots
   * would double count, so they take the latest one on or before the period end.
   */
  private get latestSnapshot(): string {
    const s = this.snapshotsIn(this.period);
    return s[s.length - 1] || "";
  }

  /**
   * This screen has no brand filter, so the topnav's brand label is cleared —
   * otherwise it would keep the brand of whichever screen published one last.
   * brand_key is null: there is no key the last-refreshed lookup could resolve.
   */
  private publishTopnavContext(): void {
    this.filterService.lastRefreshedDate.next({ brand_key: null, brand_name: "" });
  }

  // ================================================================== cards
  onShowByChange(card: CoverageCard, value: ShowBy): void {
    card.showBy = value;
    card.hidden = new Set();
    this.buildCard(card);
    this.saveState();
  }

  onGroupedChange(card: CoverageCard, grouped: boolean): void {
    card.grouped = grouped;
    card.hidden = new Set();
    this.buildCard(card);
    this.saveState();
  }

  /** Page 2's Show by: one value of the widget's field, or "" for all. */
  onSeriesShowByChange(card: SeriesCard, value: string): void {
    card.showBy = value;
    this.buildSeriesCard(card);
    this.saveState();
  }

  /** Page 4's share Show by: which measure (NBRx / TRx) the bands sum. */
  onShareShowByChange(card: ShareCard, value: string): void {
    card.showBy = value;
    card.hidden = new Set();
    this.buildShareCard(card);
    this.saveState();
  }

  /** The Y-axis choices a stacked card's Settings offers. */
  yOptionsFor(card: ParityCard | ShareCard): { key: YAxis; label: string }[] {
    return card.kind === "share" ? card.widget.yAxes : Y_AXIS_OPTIONS;
  }

  /** Settings popover opened: start from what the card is showing. */
  openSettings(card: AnyCard): void {
    card.pendingX = card.xAxis;
    card.pendingY = card.yAxis;
  }

  applySettings(card: AnyCard): void {
    const changed = card.pendingX !== card.xAxis || card.pendingY !== card.yAxis;
    card.xAxis = card.pendingX as any;
    card.yAxis = card.pendingY as any;
    if (!changed) return;
    this.rebuild(card);
    this.saveState();
  }

  /** Legend click: show/hide one series. The last visible one cannot be hidden. */
  toggleSeries(card: AnyCard, key: string): void {
    const next = new Set(card.hidden);
    if (next.has(key)) {
      next.delete(key);
    } else {
      if (next.size >= card.legend.length - 1) return;
      next.add(key);
    }
    // A new Set (not a mutation) so the chart's ngOnChanges sees it.
    card.hidden = next;
  }

  showCardHelp(card: { help: HelpEntry | null }): void {
    this.cardHelp = card.help;
  }

  /**
   * Full view: the card is pinned over the whole window (see .is-fullscreen)
   * and its chart re-sized to the window's height.
   *
   * Deliberately not the browser Fullscreen API. Material's menus and selects
   * render in the CDK overlay container at the end of <body>, which a fullscreen
   * element paints over — so Help, Settings and Show by would open invisibly.
   * A fixed card leaves that container above it and every popup keeps working.
   */
  toggleFullscreen(card: AnyCard): void {
    const prev = this.findCard(this.fullscreenId);
    this.fullscreenId = this.fullscreenId === card.widget.id ? null : card.widget.id;
    this.fullViewHeight = 0;
    if (prev && prev !== card) this.rebuild(prev);
    this.rebuild(card);
    // Once the card is laid out full-window, size the chart to what is left.
    this.refitFullView();
  }

  /** The chart's measured height in full view; 0 until measured. */
  private fullViewHeight = 0;

  /** Chart height for a card: 458px normally (the plot keeps its size with the 52px brush), the measured space in full view. */
  private chartHeightFor(card: AnyCard): number {
    if (this.fullscreenId !== card.widget.id) return 458;
    return this.fullViewHeight || Math.max(458, window.innerHeight - 170);
  }

  /**
   * Fit the full-view card's chart to the card's full height: everything from
   * the chart's own top edge (below the header, the legend and — when a bar is
   * open — the waterfall's Back row) down to the card's bottom padding. Measured
   * rather than estimated, because the legend wraps to a second line on narrow
   * windows and the Back row comes and goes. Re-run on resize and on every
   * bars ⇄ waterfall switch.
   */
  refitFullView(): void {
    setTimeout(() => {
      const card = this.findCard(this.fullscreenId);
      if (!card?.config) return;
      const el = document.querySelector(`[data-card-id="${card.widget.id}"]`) as HTMLElement | null;
      const chart = el?.querySelector(".mlwf-chart") as HTMLElement | null;
      if (!el || !chart) return;
      const padBottom = parseFloat(getComputedStyle(el).paddingBottom) || 0;
      // A few px spare so the waterfall's horizontal scrollbar never forces a
      // vertical one on the card.
      const h = Math.floor(
        el.getBoundingClientRect().bottom - chart.getBoundingClientRect().top - padBottom - 12
      );
      if (h <= 0 || h === this.fullViewHeight) return;
      this.fullViewHeight = Math.max(320, h);
      // A new config object, so the chart's ngOnChanges redraws at the new height.
      card.config = { ...card.config, chartHeight: this.fullViewHeight } as any;
      this.cdr.detectChanges();
    });
  }

  /**
   * The filter that has taken this card out of scope, or null while it is in
   * scope. Read from the card's own config: every field its `where` pins (the
   * indication, for the coverage cards) must keep at least one of its values in
   * that field's chip. Exclude PsA from the Indication chip and the PsA card
   * stays on the page — header and all — with "There is no data for the selected
   * Indication" where its legend and chart were.
   */
  outOfScopeFilter(card: AnyCard): string | null {
    // Page 2's own sources pin nothing in `where`; their scope is `honors`.
    const where: Record<string, any> = "where" in card.widget ? card.widget.where : {};
    for (const [key, want] of Object.entries(where)) {
      const f = this.filters.find((x) => x.key === key);
      if (!f || !this.hasOptions(f)) continue;
      const values = Array.isArray(want) ? want : [want];
      if (!values.some((v) => f.applied.has(String(v)))) return f.label;
    }
    return null;
  }

  // ================================================================ export
  /** "<widget> - <page>", safe as a file name. */
  private exportName(card: AnyCard): string {
    const page = this.pages.find((p) => p.key === card.widget.page)?.title || "";
    return `${card.widget.title} - ${page}`.replace(/[\\/:*?"<>|]+/g, "-").trim();
  }

  /**
   * Export to Excel: exactly what the chart is showing — one row per bar (in
   * bar order), a column per series in legend order, then total lives. In the
   * % view the series columns are shares of the bar; in No. of Lives they are
   * lives. Written through the shared ExcelExportService, as the chart widgets
   * do.
   */
  exportExcel(card: AnyCard): void {
    if (card.kind === "series") return this.exportSeriesExcel(card);
    if (!card.rows.length || !card.config) return;
    const xAxes: { key: string; axisLabel: string }[] =
      card.kind === "coverage" ? X_AXIS_OPTIONS : card.widget.xAxes;
    const x = xAxes.find((o) => o.key === card.xAxis) || xAxes[0];
    const keys = (card.config.displayKeys || card.config.barKeys).filter((k) =>
      card.config.barKeys.includes(k)
    );
    const pct = card.yAxis === "pct";

    const columns: any[] = [
      { header: x.axisLabel, key: "name", type: "string", width: 24 },
      ...keys.map((k, i) => ({
        header: pct ? `${k} (%)` : k,
        key: `s${i}`,
        type: pct ? "percent" : "number",
        width: 16,
      })),
      { header: `Total ${card.kind === "share" ? card.widget.unit : "Lives"}`, key: "total", type: "number", width: 16 },
    ];
    const rows = card.rows.map((r) => {
      const out: any = { name: r.name, total: r.total };
      keys.forEach((k, i) => {
        const v = Number(r[k]) || 0;
        out[`s${i}`] = pct ? (r.total ? Math.round((v / r.total) * 1000) / 10 : 0) : v;
      });
      return out;
    });
    this.excelService.customExportExcel(rows, this.exportName(card), columns);
  }

  /**
   * A page-2 card's export: one row per group, a column per series in legend
   * order — shares (one decimal) in the % view, counts in the No. view.
   */
  private exportSeriesExcel(card: SeriesCard): void {
    if (!card.rows.length || !card.config) return;
    const w = card.widget;
    const x = w.xAxes.find((o) => o.key === card.xAxis) || w.xAxes[0];
    const y = w.yAxes.find((o) => o.key === card.yAxis) || w.yAxes[0];
    const pct = y.key === "pct";
    const keys = card.config.keys;
    // Shares to one decimal; an index or average to its view's decimals.
    const places = pct ? 1 : y.decimals;
    const columns: any[] = [
      { header: x.axisLabel, key: "name", type: "string", width: 24 },
      ...keys.map((k, i) => ({
        header: pct ? `${k} (%)` : `${k} (${y.unit || w.unit})`,
        key: `s${i}`,
        type: pct ? "percent" : "number",
        width: 18,
      })),
    ];
    const rows = card.rows.map((r) => {
      const out: any = { name: r.name };
      keys.forEach((k, i) => {
        const v = Number(r[k]) || 0;
        out[`s${i}`] = places === undefined ? v : Number(v.toFixed(places));
      });
      return out;
    });
    this.excelService.customExportExcel(rows, this.exportName(card), columns);
  }

  /**
   * Export to Image: the card as it stands — header, legend and chart — as a
   * PNG. The header's icon row is left out of the picture. html2canvas with the
   * app's colour normaliser, the same pairing icon-chart's exportToPNG uses.
   */
  exportImage(card: AnyCard): void {
    const el = document.querySelector(`[data-card-id="${card.widget.id}"]`) as HTMLElement | null;
    if (!el) return;
    html2canvas(el, {
      useCORS: true,
      scale: 2,
      backgroundColor: "#ffffff",
      ignoreElements: (node) => node.classList?.contains("mam-icons"),
      onclone: normaliseColorsForHtml2canvas,
    })
      .then((canvas) => {
        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${this.exportName(card)}.png`;
        link.click();
      })
      .catch((err) => console.error("Export to image failed:", err));
  }

  trackCard = (_: number, c: AnyCard) => c.widget.id;
  trackKpi = (_: number, k: KpiView) => k.cfg.id;
  trackRestrictionRow = (_: number, r: RestrictionRow) => r.path;
  trackLegend = (_: number, l: LegendItem) => l.key;
  trackFilter = (_: number, f: FilterDef) => f.key;

  /**
   * Build one card from configuration: the widget's config plus the card's own
   * settings become an AggSpec, and aggregate() cuts the filtered array to it.
   * No grouping happens here — see StackedWidgetConfig for how the parts map.
   */
  private buildCard(card: CoverageCard): void {
    const w = card.widget;
    const x = X_AXIS_OPTIONS.find((o) => o.key === card.xAxis) || X_AXIS_OPTIONS[0];
    const y = Y_AXIS_OPTIONS.find((o) => o.key === card.yAxis) || Y_AXIS_OPTIONS[0];
    const where: Where<MoonlakeRecord> = { ...w.where };
    if (x.snapshots === "latest") where.snapshot = this.latestSnapshot;

    // Which lines (1L, 2L, 3L, …) this widget's rows actually contain — the
    // bands, legend and colour ramp are built for exactly those.
    const lines = linesIn(this.viewData.filter((r) => matchesWhere(r, where)));
    const keys = stackKeysFor(card.showBy, card.grouped, lines);

    card.rows = aggregate(this.viewData, {
      where,
      groupBy: x.groupBy,
      order: x.order,
      stackBy: w.stackBy,
      stackMap: (_v, r) => stackKeyFor(r, card.showBy, card.grouped),
      stackKeys: keys,
      measure: w.measure,
      fn: w.fn,
    }) as WaterfallRow[];

    const isCovered = card.showBy === "covered";
    const colours = isCovered ? COVERED_COLORS : bucketColors(lines);
    const gradients: Record<string, [string, string]> =
      !isCovered && card.grouped ? { [LINE_GROUP_KEY]: GROUPED_3L_GRADIENT } : {};

    card.config = {
      barKeys: keys,
      displayKeys: legendKeysFor(card.showBy, card.grouped, lines),
      colours,
      gradients,
      shortLabels: BUCKET_SHORT_LABELS,
      xAxisLabel: x.axisLabel,
      yAxisLabel: y.axisTitle,
      yMode: card.yAxis,
      chartHeight: this.chartHeightFor(card),
      unit: w.unit,
      valueFormat: (v: number) => shortCount(v),
      noDataMessage: "There is no data for this selection",
    };

    card.legend = legendKeysFor(card.showBy, card.grouped, lines).map((key) => {
      const g = gradients[key];
      return {
        key,
        label: key,
        swatch: g ? `linear-gradient(180deg, ${g[0]}, ${g[1]})` : colours[key],
      };
    });
  }

  // ================================================================ page 2
  /**
   * Lives-weighted rates for the focus brand: its lives in the latest snapshot
   * on or before the period end that meet the KPI's rule, over all its lives.
   */
  private buildKpis(): void {
    const latest = this.latestSnapshot;
    const rows = this.viewData.filter((r) => r.snapshot === latest && r.drug === FOCUS_DRUG);
    const total = rows.reduce((s, r) => s + r.lives, 0);
    this.kpis = this.kpis.map((k) => {
      const num = rows.reduce((s, r) => s + (k.cfg.numerator(r) ? r.lives : 0), 0);
      return { ...k, value: total ? (num / total) * 100 : null };
    });
  }

  kpiText(k: KpiView): string {
    if (k.value === null) return "–";
    if (!k.signed) return `${Math.round(k.value)}%`;
    return `${k.value > 0 ? "+" : ""}${k.value.toFixed(1)}%`;
  }

  /**
   * A non-MMIT source narrowed by what the widget honours: the list chips it
   * maps to its own fields (a value the chip does not list passes), Payer as
   * the channels of the selected formularies, and the Date range.
   */
  private sourceRows(w: SourceScope): any[] {
    if (w.source === "mmit") return this.viewData;
    const rows: any[] = this.sourceData[w.source] || [];
    const checks = Object.entries(w.honors || {})
      .map(([key, field]) => ({ f: this.filters.find((x) => x.key === key && x.kind === "list"), field }))
      .filter((c) => c.f && this.isPartial(c.f)) as { f: FilterDef; field: string }[];
    const channels = w.payerAsChannel ? this.payerChannels : null;
    // Records with the full payer path answer the Payer tree as the MMIT ones do.
    const payer = w.honorsPayer ? this.filters.find((f) => f.kind === "tree" && this.isPartial(f)) : undefined;
    const from = w.periodField && this.period.start ? quarterFromKey(this.period.start).start : "";
    const to = w.periodField && this.period.end ? quarterFromKey(this.period.end).end : "";

    return rows.filter((r) => {
      for (const { f, field } of checks) {
        const v = String(r[field] ?? "");
        if (f.options.includes(v) && !f.applied.has(v)) return false;
      }
      if (channels && !channels.has(String(r[w.payerAsChannel!]))) return false;
      if (payer && !payer.applied.has(this.valueOf(payer, r))) return false;
      if (from && (r[w.periodField!] < from || r[w.periodField!] > to)) return false;
      return true;
    });
  }

  /** Build one page-2 card from configuration, as buildCard does for page 1. */
  private buildSeriesCard(card: SeriesCard): void {
    const w = card.widget;
    const x = w.xAxes.find((o) => o.key === card.xAxis) || w.xAxes[0];
    const y = w.yAxes.find((o) => o.key === card.yAxis) || w.yAxes[0];
    const source = this.sourceRows(w);

    if (w.showBy) {
      const sb = w.showBy;
      const values = distinctValues(source, sb.field, sb.order);
      card.showByOptions = [
        { value: "", title: sb.allLabel },
        ...values.map((v) => ({ value: v, title: v })),
      ];
      if (card.showBy && !values.includes(card.showBy)) card.showBy = "";
    }

    const where: Where<any> = { ...w.where };
    if (w.source === "mmit" && x.snapshots === "latest") where["snapshot"] = this.latestSnapshot;
    if (w.showBy && card.showBy) where[w.showBy.field] = card.showBy;
    const rows = source.filter((r) => matchesWhere(r, where));

    // The series present under the filters, in configured order. Without
    // seriesBy, the one series is there whenever any row is.
    const present = new Set(
      w.seriesBy ? rows.map((r) => String(r[w.seriesBy!] ?? "")) : rows.length ? [w.series[0]] : []
    );
    const keys = w.series.filter((k) => present.has(k));
    if ([...card.hidden].some((k) => !keys.includes(k))) {
      card.hidden = new Set([...card.hidden].filter((k) => keys.includes(k)));
    }

    const measure = y.measure || w.measure;
    const denominator = y.denominator || w.denominator;
    if (y.mode === "index" && w.index && keys.length) {
      card.rows = aggregateIndex(rows, {
        groupBy: x.groupBy,
        order: x.order,
        key: keys[0],
        measure,
        denominator: denominator || measure,
        focus: w.index.focus,
      });
    } else {
      card.rows = aggregateSeries(rows, {
        groupBy: x.groupBy,
        order: x.order,
        seriesBy: w.seriesBy,
        seriesKeys: keys,
        measure,
        denominator,
        numerator: w.numerator,
        mode:
          y.mode === "ratio"
            ? "ratio"
            : y.key !== "pct"
            ? "abs"
            : w.pctOf === "group"
            ? "share-group"
            : "share-cell",
      });
    }

    const places = y.decimals;
    card.config = {
      type: w.chart,
      keys,
      colours: w.colours,
      keyNotes: w.seriesNotes,
      xAxisLabel: x.axisLabel,
      yAxisLabel: y.axisTitle,
      yMode: y.key === "pct" ? "pct" : "abs",
      chartHeight: this.chartHeightFor(card),
      unit: y.unit || w.unit,
      valueFormat: places === undefined ? (v: number) => shortCount(v) : (v: number) => v.toFixed(places),
      // An index or an average does not add up across series.
      showShare: !y.mode,
      noDataMessage: "There is no data for this selection",
    };

    card.legend = keys.map((k) => ({
      key: k,
      label: w.seriesLabels?.[k] || k,
      swatch: w.colours[k] || "#999999",
    }));
  }

  // ================================================================ page 4
  /**
   * Net switching for the focus brand: switches into it less switches away
   * from it, over every switch in scope, as a percent.
   */
  private buildRweKpis(): void {
    this.rweKpis = this.rweKpis.map((k) => {
      let into = 0;
      let away = 0;
      let all = 0;
      (this.sourceRows(k.cfg) as SwitchRecord[]).forEach((r) => {
        all += r.switched;
        if (r.to_drug === k.cfg.focus) into += r.switched;
        if (r.from_drug === k.cfg.focus) away += r.switched;
      });
      const value = all ? ((into - away) / all) * 100 : null;
      const color = value !== null && value < k.cfg.target ? k.cfg.missColor : k.cfg.color;
      return { ...k, value, color };
    });
  }

  /**
   * The focus brand's restriction index per plan (restricted ÷ requested
   * claims), highest first, with the plan's MMIT coverage of the brand: the
   * coverage carrying most of its lives in the latest snapshot.
   */
  private buildRestrictionTable(): void {
    const t = RWE_RESTRICTION_TABLE;
    const byPlan = new Map<string, RestrictionRow>();
    (this.sourceRows(t) as RxRecord[]).forEach((r) => {
      if (r.drug !== t.focus) return;
      let row = byPlan.get(r.payer_plan);
      if (!row) {
        row = {
          plan: r.payer_plan,
          path: [r.pbm, r.controller, r.plan].join(TREE_SEP),
          requests: 0,
          restricted: 0,
          index: 0,
          status: "–",
        };
        byPlan.set(r.payer_plan, row);
      }
      row.requests += r.requests;
      row.restricted += r.restricted;
    });

    const latest = this.latestSnapshot;
    const coverage = new Map<string, Map<string, number>>();
    (this.data as CompetitiveRecord[]).forEach((r) => {
      if (r.snapshot !== latest || r.drug !== t.focus || !r.coverage) return;
      if (!this.filters.every((f) => t.statusIgnores.includes(f.key) || this.rowPasses(f, r))) return;
      const plan = payerPlanLabel(r);
      if (!coverage.has(plan)) coverage.set(plan, new Map());
      const lives = coverage.get(plan)!;
      lives.set(r.coverage, (lives.get(r.coverage) || 0) + r.lives);
    });

    this.restrictionRows = [...byPlan.values()]
      .filter((row) => row.requests > 0)
      .map((row) => {
        const lives = coverage.get(row.plan);
        const status = lives ? [...lives.entries()].sort((a, b) => b[1] - a[1])[0][0] : "–";
        return { ...row, index: row.restricted / row.requests, status };
      })
      .sort((a, b) => b.index - a.index || a.plan.localeCompare(b.plan));
    this.restrictionPager.index = 0;
  }

  /** Export to Excel: the table as shown. */
  exportRestrictionTable(): void {
    if (!this.restrictionRows.length) return;
    const columns: any[] = [
      { header: "Plan", key: "plan", type: "string", width: 36 },
      { header: "Restriction Index", key: "index", type: "number", width: 18 },
      { header: "Rx Requests", key: "requests", type: "number", width: 14 },
      { header: "Restricted", key: "restricted", type: "number", width: 14 },
      { header: "MMIT Status", key: "status", type: "string", width: 24 },
    ];
    const rows = this.restrictionRows.map((r) => ({
      plan: r.plan,
      index: Number(r.index.toFixed(2)),
      requests: r.requests,
      restricted: r.restricted,
      status: r.status,
    }));
    const page = this.pages.find((p) => p.key === RWE_RESTRICTION_TABLE.page)?.title || "";
    const name = `${RWE_RESTRICTION_TABLE.title} - ${page}`.replace(/[\\/:*?"<>|]+/g, "-");
    this.excelService.customExportExcel(rows, name, columns);
  }

  /**
   * A share card: the chosen measure summed per X value, stacked by the
   * widget's field — the focus brand first, then the rest alphabetically.
   * Colours are fixed per value across the whole source, so a band keeps its
   * colour as the filters narrow the set.
   */
  private buildShareCard(card: ShareCard): void {
    const w = card.widget;
    const x = w.xAxes.find((o) => o.key === card.xAxis) || w.xAxes[0];
    const y = w.yAxes.find((o) => o.key === card.yAxis) || w.yAxes[0];
    const measure = (w.measures.find((m) => m.key === card.showBy) || w.measures[0]).key;
    const rows = this.sourceRows(w);

    const present = distinctValues(rows, w.stackBy);
    const keys = [
      ...w.stackFirst.filter((k) => present.includes(k)),
      ...present.filter((k) => !w.stackFirst.includes(k)),
    ];
    if ([...card.hidden].some((k) => !keys.includes(k))) {
      card.hidden = new Set([...card.hidden].filter((k) => keys.includes(k)));
    }

    card.rows = aggregate<any>(rows, {
      groupBy: x.groupBy,
      order: x.order,
      stackBy: w.stackBy,
      stackKeys: keys,
      measure,
      fn: "sum",
    }) as WaterfallRow[];

    const all = distinctValues(this.sourceData[w.source] || [], w.stackBy);
    const colours =
      w.colours ||
      drugColours([...w.stackFirst.filter((k) => all.includes(k)), ...all.filter((k) => !w.stackFirst.includes(k))]);
    card.config = {
      barKeys: keys,
      colours,
      xAxisLabel: x.axisLabel,
      yAxisLabel: y.axisTitle,
      yMode: card.yAxis,
      chartHeight: this.chartHeightFor(card),
      unit: w.unit,
      valueFormat: (v: number) => shortCount(v),
      noDataMessage: "There is no data for this selection",
    };
    card.legend = keys.map((k) => ({ key: k, label: k, swatch: colours[k] }));
  }

  // ================================================================ grids
  /** A grid's pager on its saved page size (when still offered), first page. */
  private savedPager(id: string): Pager {
    const size = this.savedState.cards?.[id]?.pageSize;
    return { size: size && TABLE_PAGE_SIZES.includes(size) ? size : TABLE_PAGE_SIZE, index: 0 };
  }

  pageCount(p: Pager, total: number): number {
    return Math.max(1, Math.ceil(total / p.size));
  }

  /** 1-based number of the first / last row on the page, for "1 to 10 of 34". */
  pageFirstRow(p: Pager, total: number): number {
    return total ? p.index * p.size + 1 : 0;
  }

  pageLastRow(p: Pager, total: number): number {
    return Math.min((p.index + 1) * p.size, total);
  }

  goToPage(p: Pager, index: number, total: number): void {
    p.index = Math.min(Math.max(index, 0), this.pageCount(p, total) - 1);
  }

  /** A new page size starts again on the first page. */
  setPageSize(p: Pager, size: number): void {
    p.size = size;
    p.index = 0;
    this.saveState();
  }

  /** The share cards a page shows. */
  shareCardsOn(page: string): ShareCard[] {
    return this.shareCards.filter((c) => c.widget.page === page);
  }

  // ================================================================ page 5
  /**
   * A formulary table for its picked drug: one row per formulary that has the
   * drug's lives under the filters (latest snapshot), its cells read off any of
   * its records (they agree on formulary-level fields), largest lives first.
   */
  private buildFormularyTable(t: FormularyTableView): void {
    const latest = this.latestSnapshot;
    const rows = this.viewData.filter((r) => r.snapshot === latest);
    t.drugOptions = distinctValues(rows, "drug");
    if (!t.drugOptions.includes(t.drug)) {
      t.drug = t.drugOptions.includes(t.cfg.defaultDrug) ? t.cfg.defaultDrug : t.drugOptions[0] || "";
    }

    const byFormulary = new Map<string, FormularyTableRow>();
    rows.forEach((r) => {
      if (r.drug !== t.drug) return;
      const k = this.formularyKey(r);
      const row = byFormulary.get(k);
      if (row) {
        row.lives += r.lives;
        return;
      }
      byFormulary.set(k, {
        path: [r.pbm, r.controller, r.plan, r.formulary].join(TREE_SEP),
        indication: r.indication,
        lives: r.lives,
        // The grid has no Indication column: a formulary listed once per indication
        // is told apart on hover.
        title: `${r.indication} — ${
          t.cfg.rowTitle ? t.cfg.rowTitle(r) : [r.pbm, r.controller, r.plan, r.formulary].join(TREE_SEP)
        }`,
        cells: t.cfg.cells(r),
      });
    });

    t.rows = [...byFormulary.values()].sort((a, b) => b.lives - a.lives || a.path.localeCompare(b.path));
    t.pager.index = 0;
  }

  onFormularyTableDrugChange(t: FormularyTableView, drug: string): void {
    t.drug = drug;
    this.buildFormularyTable(t);
    this.saveState();
  }

  /** Export to Excel: the table as shown, with each formulary's lives. */
  exportFormularyTable(t: FormularyTableView): void {
    if (!t.rows.length) return;
    const [first, ...rest] = t.cfg.columns;
    const columns: any[] = [
      { header: first.header, key: first.key, type: "string", width: first.width },
      { header: "Indication", key: "indication", type: "string", width: 12 },
      { header: "Lives", key: "lives", type: "number", width: 14 },
      ...rest.map((c) => ({ header: c.header, key: c.key, type: "string", width: c.width })),
    ];
    const rows = t.rows.map((r) => ({ ...r.cells, indication: r.indication, lives: r.lives }));
    const page = this.pages.find((p) => p.key === t.cfg.page)?.title || "";
    const name = `${t.cfg.title} - ${t.drug} - ${page}`.replace(/[\\/:*?"<>|]+/g, "-");
    this.excelService.customExportExcel(rows, name, columns);
  }

  trackFormularyTable = (_: number, t: FormularyTableView) => t.cfg.id;
  trackFormularyRow = (_: number, r: FormularyTableRow) => r.path + "|" + r.indication;

  // ================================================================ page 3
  /** The latest snapshot's formulary key, per indication: the parity reference's scope. */
  private formularyKey(r: MoonlakeRecord): string {
    return [r.indication, r.pbm, r.controller, r.plan, r.formulary].join(TREE_SEP);
  }

  /**
   * Relative position: tag each of the latest snapshot's rows At Parity Or Better
   * / Worse Than Parity against its formulary's reference — the lives-weighted
   * median position of the whole competitor set there — then stack as page 1.
   */
  private buildParityCard(card: ParityCard): void {
    const w = card.widget;
    const x = w.xAxes.find((o) => o.key === card.xAxis) || w.xAxes[0];
    const y = Y_AXIS_OPTIONS.find((o) => o.key === card.yAxis) || Y_AXIS_OPTIONS[0];
    const latest = this.latestSnapshot;

    // The reference ignores the chips that only choose which drugs to show.
    const refRows = this.data.filter(
      (r) =>
        r.snapshot === latest &&
        this.filters.every((f) => w.referenceIgnores.includes(f.key) || this.rowPasses(f, r))
    );
    const byFormulary = new Map<string, { rank: number; lives: number }[]>();
    refRows.forEach((r) => {
      const k = this.formularyKey(r);
      if (!byFormulary.has(k)) byFormulary.set(k, []);
      byFormulary.get(k)!.push({ rank: positionRank(r), lives: r.lives });
    });
    const reference = new Map<string, number>();
    byFormulary.forEach((list, k) => {
      list.sort((a, b) => a.rank - b.rank);
      const half = list.reduce((s, e) => s + e.lives, 0) / 2;
      let cum = 0;
      const median = list.find((e) => (cum += e.lives) >= half) || list[list.length - 1];
      reference.set(k, median.rank);
    });

    const tagged = this.viewData
      .filter((r) => r.snapshot === latest)
      .map((r) => {
        const ref = reference.get(this.formularyKey(r)) ?? Infinity;
        return { ...r, parity: positionRank(r) <= ref ? PARITY_BETTER : PARITY_WORSE };
      });

    card.rows = aggregate<any>(tagged, {
      groupBy: x.groupBy,
      order: x.order,
      stackBy: "parity",
      stackKeys: PARITY_KEYS,
      measure: "lives",
      fn: "sum",
    }) as WaterfallRow[];

    card.config = {
      barKeys: PARITY_KEYS,
      colours: PARITY_COLORS,
      xAxisLabel: x.axisLabel,
      yAxisLabel: y.axisTitle,
      yMode: card.yAxis,
      chartHeight: this.chartHeightFor(card),
      unit: w.unit,
      valueFormat: (v: number) => shortCount(v),
      noDataMessage: "There is no data for this selection",
    };
    card.legend = PARITY_KEYS.map((k) => ({ key: k, label: k, swatch: PARITY_COLORS[k] }));
  }

  /**
   * The step-through table for the picked drug: a row per formulary that has
   * its lives under the filters (latest snapshot), with the formulary's coverage,
   * position and MMIT step text, largest first.
   */
  private buildStepTable(): void {
    const latest = this.latestSnapshot;
    const rows = this.viewData.filter((r) => r.snapshot === latest);
    this.stepDrugOptions = distinctValues(rows, "drug");
    if (!this.stepDrugOptions.includes(this.stepDrug)) {
      this.stepDrug = this.stepDrugOptions.includes(STEP_TABLE.defaultDrug)
        ? STEP_TABLE.defaultDrug
        : this.stepDrugOptions[0] || "";
    }

    // One row per formulary: its lives summed, its step edit read off any record
    // (a formulary's coverage of a drug is the same on all of them).
    const byFormulary = new Map<string, StepRow>();
    (rows as CompetitiveRecord[]).forEach((r) => {
      if (r.drug !== this.stepDrug) return;
      const k = this.formularyKey(r);
      const row = byFormulary.get(k);
      if (row) {
        row.lives += r.lives;
        return;
      }
      byFormulary.set(k, {
        path: [r.pbm, r.controller, r.plan, r.formulary].join(TREE_SEP),
        controller: r.controller,
        planFormulary: `${r.plan} / ${r.formulary}`,
        indication: r.indication,
        lives: r.lives,
        coverage: r.coverage,
        position: r.access_position,
        stepText: r.step_text,
      });
    });

    this.stepRows = [...byFormulary.values()].sort(
      (a, b) => b.lives - a.lives || a.path.localeCompare(b.path)
    );
    this.stepShowIndication = new Set(this.stepRows.map((r) => r.indication)).size > 1;
    this.stepPager.index = 0;
  }

  onStepDrugChange(drug: string): void {
    this.stepDrug = drug;
    this.buildStepTable();
    this.saveState();
  }

  /** Export to Excel: the table as shown. */
  exportStepTable(): void {
    if (!this.stepRows.length) return;
    const columns: any[] = [
      { header: "Plan / Formulary", key: "plan", type: "string", width: 44 },
      ...(this.stepShowIndication ? [{ header: "Indication", key: "indication", type: "string", width: 12 }] : []),
      { header: "Lives", key: "lives", type: "number", width: 14 },
      { header: "Coverage", key: "coverage", type: "string", width: 22 },
      { header: "Access Position", key: "position", type: "string", width: 18 },
      { header: "Step-Through Requirement (MMIT text)", key: "step", type: "string", width: 56 },
    ];
    const rows = this.stepRows.map((r) => ({
      plan: `${r.controller} — ${r.planFormulary}`,
      indication: r.indication,
      lives: r.lives,
      coverage: r.coverage,
      position: r.position,
      step: r.stepText,
    }));
    const page = this.pages.find((p) => p.key === STEP_TABLE.page)?.title || "";
    const name = `${STEP_TABLE.title} - ${this.stepDrug} - ${page}`.replace(/[\\/:*?"<>|]+/g, "-");
    this.excelService.customExportExcel(rows, name, columns);
  }

  trackStepRow = (_: number, r: StepRow) => r.path + "|" + r.indication;

  // ========================================================== persistence
  private get storageKey(): string {
    return `market-access-moonlake-filter-state:${this.dashboardKey || "default"}`;
  }

  private readState(): SavedState {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || "{}") || {};
    } catch {
      return {};
    }
  }

  /** Re-apply the saved chip selections, keeping only values still on offer. */
  private restoreFilterState(): void {
    const saved = this.savedState.filters;
    if (!saved) return;
    this.filters.forEach((f) => {
      if (f.kind === "display") return;
      const values = saved[f.key];
      if (!Array.isArray(values)) return;
      const valid = values.filter((v) => f.options.includes(v));
      const restored = f.single ? valid.slice(0, 1) : valid;
      if (!restored.length) return;
      f.applied = new Set(restored);
      f.checked = [...f.applied];
    });
  }

  private saveState(): void {
    if (!this.stateReady) return;
    const cards: Record<string, SavedCard> = {};
    this.cards.forEach((c) => {
      cards[c.widget.id] = {
        showBy: c.showBy,
        grouped: c.grouped,
        xAxis: c.xAxis,
        yAxis: c.yAxis,
      };
    });
    this.seriesCards.forEach((c) => {
      cards[c.widget.id] = { showBy: c.showBy, xAxis: c.xAxis, yAxis: c.yAxis };
    });
    this.parityCards.forEach((c) => {
      cards[c.widget.id] = { xAxis: c.xAxis, yAxis: c.yAxis };
    });
    [this.rweIndexCard, this.rweSwitchCard].forEach((c) => {
      cards[c.widget.id] = { showBy: c.showBy, xAxis: c.xAxis, yAxis: c.yAxis };
    });
    this.shareCards.forEach((c) => {
      cards[c.widget.id] = { showBy: c.showBy, xAxis: c.xAxis, yAxis: c.yAxis };
    });
    this.formularyTables.forEach((t) => (cards[t.cfg.id] = { showBy: t.drug, pageSize: t.pager.size }));
    cards[RWE_RESTRICTION_TABLE.id] = { pageSize: this.restrictionPager.size };
    cards[STEP_TABLE.id] = { showBy: this.stepDrug, pageSize: this.stepPager.size };
    const filters: Record<string, string[]> = {};
    this.filters.forEach((f) => (filters[f.key] = [...f.applied]));
    const state: SavedState = { filters, period: { ...this.period }, page: this.activePage, cards };
    this.savedState = state;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch {
      /* storage unavailable — the screen still works, it just won't remember */
    }
  }

  private clearState(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      /* nothing to clear */
    }
  }
}
